// @ts-nocheck — migrated from JS; reactstrap / react-select / layout prop friction.
import React, { Fragment, useMemo } from "react";
import IntlMessages from "Util/IntlMessages";
import { useIntl } from "react-intl";
import {
  Row,
  Card,
  CardBody,
  CardTitle,
  Badge,
  Progress,
  Button
} from "reactstrap";
import { Colxx, Separator } from "Components/CustomBootstrap";
import BreadcrumbContainer from "Components/BreadcrumbContainer";
import ReactTable from "react-table";
import DataTablePagination from "Components/DataTables/pagination";
import Chart from "react-apexcharts";
import { Sparklines, SparklinesLine } from "react-sparklines";
import { useKpiSparklineLineStyle } from "Hooks/useKpiSparklineLineStyle";
import { ReactifyChartColors } from "Util/ReactifyChartColors";
import { addCommas } from "Util/Utils";
import data from "Data/dashboardFinance.json";

import "react-table/react-table.css";

function moneyM(n) {
  return `$${addCommas(Math.round(n / 1000))}k`;
}

function moneyFull(n) {
  return `$${addCommas(Math.round(n))}`;
}

function FinanceDashboard() {
  const colors = useMemo(() => ReactifyChartColors(), []);
  const kpiSparkLineStyle = useKpiSparklineLineStyle(colors.ReactifyChartColor1);
  const intl = useIntl();

  const invoiceColumns = useMemo(() => {
    const { formatMessage } = intl;
    return [
      {
        Header: formatMessage({ id: "finance.col.invoice" }),
        accessor: "id",
        Cell: p => <span className="font-weight-medium">{p.value}</span>
      },
      { Header: formatMessage({ id: "finance.col.customer" }), accessor: "customer" },
      {
        Header: formatMessage({ id: "finance.col.amount" }),
        accessor: "amount",
        Cell: p => moneyFull(p.value)
      },
      { Header: formatMessage({ id: "finance.col.due" }), accessor: "due" },
      {
        Header: formatMessage({ id: "finance.col.status" }),
        accessor: "status",
        Cell: p => (
          <Badge
            color={
              p.value === "Overdue"
                ? "danger"
                : p.value === "Paid"
                  ? "success"
                  : "warning"
            }
          >
            {p.value}
          </Badge>
        )
      }
    ];
    }, [intl]);

  const cashflowOptions = useMemo(() => {
    const c = colors;
    return {
      chart: { toolbar: { show: false }, stacked: false },
      stroke: { width: [3, 2, 2], curve: "smooth" },
      colors: [c.ReactifyChartColor1, c.ReactifyChartColor4, c.ReactifyChartColor2],
      dataLabels: { enabled: false },
      legend: { position: "top" },
      xaxis: {
        categories: data.cashflow.categories,
        labels: { style: { colors: c.primaryColor } }
      },
      yaxis: {
        labels: {
          style: { colors: c.primaryColor },
          formatter: v => `${v}k`
        }
      },
      grid: { borderColor: c.separatorColor },
      tooltip: { theme: "light" }
    };
    }, [colors]);

  const budgetOptions = useMemo(() => {
    const c = colors;
    return {
      chart: { toolbar: { show: false } },
      plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
      colors: [c.ReactifyChartColor2, c.ReactifyChartColor1],
      dataLabels: { enabled: false },
      legend: { position: "top" },
      xaxis: {
        categories: data.budgetVsActual.map(b => b.category),
        labels: { style: { colors: c.primaryColor } }
      },
      yaxis: {
        labels: {
          style: { colors: c.primaryColor },
          formatter: v => `${Math.round(Number(v) / 1000)}k`
        }
      },
      grid: { borderColor: c.separatorColor },
      tooltip: { theme: "light" }
    };
    }, [colors]);

  const expenseOptions = useMemo(() => {
    const c = colors;
    return {
      labels: data.expenseMix.map(e => e.name),
      colors: [
        c.ReactifyChartColor1,
        c.ReactifyChartColor2,
        c.ReactifyChartColor3,
        c.ReactifyChartColor4,
        c.ReactifyChartColor5
      ],
      legend: { position: "bottom" },
      plotOptions: { pie: { donut: { size: "65%" } } },
      dataLabels: { enabled: true }
    };
    }, [colors]);

  const forecastOptions = useMemo(() => {
    const c = colors;
    return {
      chart: { toolbar: { show: false }, zoom: { enabled: false } },
      stroke: { width: [3, 3], curve: "stepline" },
      colors: [c.ReactifyChartColor1, c.ReactifyChartColor4],
      dataLabels: { enabled: false },
      legend: { position: "top" },
      xaxis: {
        categories: data.forecast.categories,
        labels: { style: { colors: c.primaryColor } }
      },
      yaxis: {
        labels: {
          style: { colors: c.primaryColor },
          formatter: v => `${v}M`
        }
      },
      grid: { borderColor: c.separatorColor },
      tooltip: { theme: "light" }
    };
    }, [colors]);

  const { formatMessage } = intl;
  const k = data.kpis;
  const pl = data.plSummary;

  return (
      <div className="dashboard-vertical dashboard-vertical--finance">
        <Fragment>
          <Row>
            <Colxx xxs="12">
              <BreadcrumbContainer
                heading={<IntlMessages id="menu.finance" />}
              />
              <Separator className="mb-5" />
            </Colxx>
          </Row>

          <Row>
            <Colxx xxs="12" className="mb-3">
              {data.alerts.map((a, i) => (
                <div
                  key={i}
                  className={`alert alert-${a.level === "danger" ? "danger" : "warning"} py-2 mb-2`}
                  role="status"
                >
                  {a.text}
                </div>
              ))}
            </Colxx>
          </Row>

          <Row className="mb-3">
            <Colxx xxs="12" className="d-flex flex-wrap gap-2">
              <Button color="primary" size="sm">
                <IntlMessages id="finance.toolbar.close-period" />
              </Button>
              <Button color="outline-secondary" size="sm">
                <IntlMessages id="finance.toolbar.reports" />
              </Button>
            </Colxx>
          </Row>

          <Row>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <Sparklines data={data.cashSpark} margin={6}>
                    <SparklinesLine style={kpiSparkLineStyle} />
                  </Sparklines>
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="finance.kpi.cash" />
                  </p>
                  <h4 className="mb-0">{moneyFull(k.cash)}</h4>
                  <span className="text-success small">+{k.cashDelta}%</span>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-calendar text-primary mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="finance.kpi.runway" />
                  </p>
                  <h4 className="mb-0">
                    {k.runwayMonths}{" "}
                    <span className="h6 text-muted">
                      <IntlMessages id="finance.kpi.months" />
                    </span>
                  </h4>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-fire text-danger mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="finance.kpi.burn" />
                  </p>
                  <h4 className="mb-0">{moneyFull(k.burn)}</h4>
                  <span className="text-success small">{k.burnDelta}%</span>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-line-chart text-success mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="finance.kpi.revenue-ytd" />
                  </p>
                  <h4 className="mb-0">{moneyM(k.revenueYtd)}</h4>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-tachometer text-info mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="finance.kpi.ebitda" />
                  </p>
                  <h4 className="mb-0">{moneyM(k.ebitda)}</h4>
                  <span className="text-muted small">{k.ebitdaMargin}%</span>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-arrow-circle-down text-warning mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="finance.kpi.ar" />
                  </p>
                  <h4 className="mb-0">{moneyM(k.ar)}</h4>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-arrow-circle-up text-secondary mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="finance.kpi.ap" />
                  </p>
                  <h4 className="mb-0">{moneyM(k.ap)}</h4>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-balance-scale text-primary mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="finance.kpi.net-income" />
                  </p>
                  <h4 className="mb-0">{moneyM(pl.netIncome)}</h4>
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx lg="4" className="mb-4">
              <Card className="h-100 dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="finance.section.pl" />
                  </CardTitle>
                  <ul className="list-unstyled mb-0">
                    <li className="d-flex justify-content-between py-2 border-bottom">
                      <span>
                        <IntlMessages id="finance.pl.revenue" />
                      </span>
                      <span className="font-weight-medium">
                        {moneyM(pl.revenue)}
                      </span>
                    </li>
                    <li className="d-flex justify-content-between py-2 border-bottom">
                      <span>
                        <IntlMessages id="finance.pl.cogs" />
                      </span>
                      <span>{moneyM(pl.cogs)}</span>
                    </li>
                    <li className="d-flex justify-content-between py-2 border-bottom">
                      <span>
                        <IntlMessages id="finance.pl.opex" />
                      </span>
                      <span>{moneyM(pl.opex)}</span>
                    </li>
                    <li className="d-flex justify-content-between py-2 border-bottom">
                      <span>
                        <IntlMessages id="finance.pl.other" />
                      </span>
                      <span>{moneyM(pl.other)}</span>
                    </li>
                    <li className="d-flex justify-content-between py-2 pt-3">
                      <span className="font-weight-bold">
                        <IntlMessages id="finance.pl.net" />
                      </span>
                      <span className="font-weight-bold text-success">
                        {moneyM(pl.netIncome)}
                      </span>
                    </li>
                  </ul>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="8" className="mb-4">
              <Card className="dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="finance.chart.cashflow" />
                  </CardTitle>
                  <Chart
                    options={cashflowOptions}
                    series={[
                      { name: "Operating", data: data.cashflow.operating },
                      { name: "Investing", data: data.cashflow.investing },
                      { name: "Financing", data: data.cashflow.financing }
                    ]}
                    type="line"
                    height={300}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx lg="6" className="mb-4">
              <Card className="h-100 dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="finance.chart.budget" />
                  </CardTitle>
                  <Chart
                    options={budgetOptions}
                    series={[
                      {
                        name: formatMessage({ id: "finance.legend.budget" }),
                        data: data.budgetVsActual.map(b => b.budget)
                      },
                      {
                        name: formatMessage({ id: "finance.legend.actual" }),
                        data: data.budgetVsActual.map(b => b.actual)
                      }
                    ]}
                    type="bar"
                    height={320}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="finance.chart.expenses" />
                  </CardTitle>
                  <Chart
                    options={expenseOptions}
                    series={data.expenseMix.map(e => e.value)}
                    type="donut"
                    height={300}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="3" className="mb-4">
              <Card className="h-100">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="finance.section.aging" />
                  </CardTitle>
                  {data.agingAr.map((b, i) => (
                    <div key={i} className="mb-3">
                      <div className="d-flex justify-content-between small mb-1">
                        <span>{b.bucket}</span>
                        <span>{moneyM(b.amount)}</span>
                      </div>
                      <Progress
                        value={
                          (b.amount /
                            data.agingAr.reduce((s, x) => s + x.amount, 0)) *
                          100
                        }
                      />
                    </div>
                  ))}
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx lg="8" className="mb-4">
              <Card>
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="finance.table.invoices" />
                  </CardTitle>
                  <ReactTable
                    defaultPageSize={6}
                    data={data.invoices}
                    columns={invoiceColumns}
                    minRows={0}
                    PaginationComponent={DataTablePagination}
                  />
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="4" className="mb-4">
              <Card className="h-100 dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="finance.chart.forecast" />
                  </CardTitle>
                  <Chart
                    options={forecastOptions}
                    series={[
                      {
                        name: formatMessage({ id: "finance.legend.revenue" }),
                        data: data.forecast.revenue
                      },
                      {
                        name: formatMessage({ id: "finance.legend.expenses" }),
                        data: data.forecast.expenses
                      }
                    ]}
                    type="line"
                    height={280}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
          </Row>
        </Fragment>
      </div>
    );
}

export default FinanceDashboard;
